import { usePlans, PlanGrid, IntervalToggle } from "@/components/PlanCards";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, UserRound, Trees, Ruler, Palette, FolderLock, Download, Languages, Gauge, PenLine } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useI18n } from "@/i18n";
import { api, API } from "@/lib/api";
import { SiteHeader, SiteFooter } from "@/components/site";
import { useMeta } from "@/lib/useMeta";

const HERO_IMG = "https://images.unsplash.com/photo-1705326454924-f6777522b030?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80";
const TOOL_ICONS = { hero: Sparkles, model: UserRound, lifestyle: Trees, dimensions: Ruler };
const FEATURE_ICONS = [Palette, FolderLock, Download, PenLine, Languages, Gauge];

function Hero() {
  const { t } = useI18n();
  return (
    <section className="relative overflow-hidden grain">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <p className="eyebrow mb-5">{t("landing.eyebrow")}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04]" data-testid="landing-title">{t("landing.title")}</h1>
          <p className="mt-6 text-base md:text-lg text-neutral-300 max-w-xl">{t("landing.subtitle")}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/register" className="btn-brand rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2" data-testid="hero-cta-primary">
              {t("landing.ctaPrimary")} <ArrowRight size={18} />
            </Link>
            <a href="#pricing" className="rounded-full px-6 py-3 font-semibold border border-[var(--line)] hover:border-neutral-500 transition-colors" data-testid="hero-cta-secondary">
              {t("landing.ctaSecondary")}
            </a>
          </div>
        </div>
        <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "120ms" }}>
          <div className="light-panel p-3 rotate-[1.5deg] shadow-2xl shadow-black/50">
            <img src={HERO_IMG} alt="" className="rounded-lg w-full h-[420px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 panel px-4 py-3 text-sm flex items-center gap-3 shadow-xl">
            <span className="h-6 w-6 rounded" style={{ background: "#F4EFE8" }} />
            <span className="font-mono text-neutral-300">#F4EFE8</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Tools() {
  const { t } = useI18n();
  return (
    <section id="features" className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
      <h2 className="text-3xl sm:text-4xl font-semibold">{t("landing.toolsTitle")}</h2>
      <p className="text-neutral-400 mt-2 text-base md:text-lg">{t("landing.toolsSub")}</p>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.keys(TOOL_ICONS).map((k, i) => {
          const Icon = TOOL_ICONS[k];
          return (
            <div key={k} className="panel p-6 hover:border-emerald-700/60 transition-colors duration-300 fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`tool-card-${k}`}>
              <Icon className="text-emerald-400" size={22} />
              <h3 className="mt-5 text-lg font-semibold">{t(`tools.${k}.name`)}</h3>
              <p className="mt-2 text-sm text-neutral-400">{t(`tools.${k}.desc`)}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Showcase() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/public/showcase").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20" data-testid="showcase-section">
      <h2 className="text-3xl sm:text-4xl font-semibold">{t("landing.showcaseTitle")}</h2>
      <p className="text-neutral-400 mt-2">{t("landing.showcaseSub")}</p>
      {items && items.length === 0 && <p className="mt-8 panel p-8 text-neutral-400 text-sm" data-testid="showcase-empty">{t("landing.showcaseEmpty")}</p>}
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        {(items || []).map((it) => (
          <div key={it.id} className="grid grid-cols-2 gap-2 panel p-2" data-testid={`showcase-item-${it.id}`}>
            {[["before", it.before], ["after", it.after]].map(([k, id]) => (
              <figure key={k} className="relative">
                <img src={`${API}/public/showcase/file/${id}`} alt={t(`landing.${k}`)} loading="lazy" className="rounded-lg aspect-square object-cover w-full bg-white" />
                <figcaption className="absolute top-2 left-2 text-[11px] px-2 py-0.5 rounded-full bg-black/70">{t(`landing.${k}`)}</figcaption>
              </figure>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function How() {
  const { t } = useI18n();
  return (
    <section id="how" className="border-y border-[var(--line)] bg-[var(--bg-1)]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <h2 className="text-3xl sm:text-4xl font-semibold">{t("landing.howTitle")}</h2>
        <ol className="mt-10 grid md:grid-cols-4 gap-8">
          {t("landing.how").map(([title, d], i) => (
            <li key={i}>
              <span className="font-display text-5xl text-emerald-500/80">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-3 font-semibold text-lg">{title}</h3>
              <p className="text-sm text-neutral-400 mt-1">{d}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Features() {
  const { t } = useI18n();
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
      <h2 className="text-3xl sm:text-4xl font-semibold">{t("landing.featuresTitle")}</h2>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
        {t("landing.features").map(([title, d], i) => {
          const Icon = FEATURE_ICONS[i];
          return (
            <div key={i} className="flex gap-4">
              <Icon className="text-emerald-400 shrink-0 mt-1" size={20} />
              <div>
                <h3 className="font-semibold">{title}</h3>
                <p className="text-sm text-neutral-400 mt-1">{d}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Pricing() {
  const { t } = useI18n();
  const data = usePlans();
  const [interval, setIv] = useState("monthly");
  return (
    <section id="pricing" className="max-w-7xl mx-auto px-5 sm:px-8 py-20" data-testid="pricing-section">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div className="max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-semibold">{t("landing.pricingTitle")}</h2>
          <p className="text-neutral-400 mt-3 text-base md:text-lg">{t("landing.pricingSub")}</p>
        </div>
        <IntervalToggle value={interval} onChange={setIv} />
      </div>
      {data && <PlanGrid plans={data.items} interval={interval} action={(p, price) => !price ? <span className="block h-12" data-testid={`pricing-cta-${p.id}`} /> : (
        <Link to="/register" className={`w-full rounded-full py-3 font-semibold inline-flex justify-center ${p.popular ? "btn-brand" : "border border-neutral-300 text-neutral-900 hover:border-emerald-600"}`} data-testid={`pricing-cta-${p.id}`}>{t("landing.ctaPrimary")}</Link>
      )} />}
      <div className="mt-6 flex flex-wrap gap-x-6 gap-y-1 text-xs text-neutral-500">
        {data && <span data-testid="pricing-trial">{t("landing.trial", { n: data.trial_images })}</span>}
        {interval === "yearly" && <span>{t("plans.yearlyNote")}</span>}
        <span>{t(interval === "yearly" ? "plans.renewYearly" : "landing.renewNote")}</span>
      </div>
    </section>
  );
}

function Faq() {
  const { t } = useI18n();
  return (
    <section id="faq" className="max-w-3xl mx-auto px-5 sm:px-8 py-20">
      <h2 className="text-3xl sm:text-4xl font-semibold">{t("landing.faqTitle")}</h2>
      <Accordion type="single" collapsible className="mt-8">
        {t("landing.faq").map(([q, a], i) => (
          <AccordionItem key={i} value={`f${i}`} data-testid={`faq-item-${i}`}>
            <AccordionTrigger className="text-left">{q}</AccordionTrigger>
            <AccordionContent className="text-neutral-400">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

export default function Landing() {
  const { t } = useI18n();
  useMeta(t("meta.home.title"), t("meta.home.desc"), { page: "home" });
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <SiteHeader />
      <Hero />
      <Tools />
      <Showcase />
      <How />
      <Features />
      <Pricing />
      <Faq />
      <SiteFooter />
    </div>
  );
}
